import React, { useMemo, useCallback, memo } from 'react';
import { Dropdown, MenuItem } from 'components';
import { useSelector, useDispatch } from 'react-redux';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { panelsSelector, selectCurrentPanel, setCurrentPanelId } from 'store';

interface Props {
  className?: string;
  selected?: string;
}

const useStyles = createUseStyles((theme: Theme) => ({
  lastItem: {
    borderTop: `1px solid ${theme.colors.grayBorder}`,
  },
  dropdownItem: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
}));

export const PanelSelector = memo(({ className, selected }: Props) => {
  const panels = useSelector(panelsSelector);
  const navigate = useNavigate();

  const theme: Theme = useTheme();
  const classes = useStyles({ theme });

  const options = useMemo(
    () =>
      panels.map((panel) => ({
        label: panel.name,
        value: panel.id,
      })),
    [panels],
  );

  const onChange = useCallback(
    (id: string) => navigate(`/new-panel/${id}`),
    [navigate],
  );

  const goToSettings = useCallback(() => {
    navigate('/settings/panels');
  }, [navigate]);

  if (!selected || panels.length < 1) {
    return null;
  }

  return (
    <Dropdown
      className={className}
      options={options}
      value={selected}
      onChange={onChange}
    >
      <div className={classes.lastItem}>
        <MenuItem onClick={goToSettings}>
          <div className={classes.dropdownItem}>
            <i className={classnames('ri-settings-3-line', classes.icon)} />
            <Trans i18nKey="participants.list.panelButton.settings" />
          </div>
        </MenuItem>
      </div>
    </Dropdown>
  );
});
